import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, Loading, EmptyState } from "@/components/common";
import { ELIGIBILITY, deadlineMessage, fmtMeetingDate } from "@/lib/meetingsApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, CheckCircle2, AlertTriangle, ChevronUp, ChevronDown, Trash2, Eye, Send, ListChecks, Search,
} from "lucide-react";

export default function AgendaWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agenda, setAgenda] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, e, r] = await Promise.all([
        api.get(`/meetings/${id}/agenda`),
        api.get(`/meetings/${id}/eligible-insights`),
        api.get(`/meetings/${id}/readiness`),
      ]);
      setMeeting(a.data.meeting); setAgenda(a.data.agenda);
      setEligible(e.data); setReadiness(r.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Clerk access required for the Agenda Builder.");
      setAgenda([]);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!agenda || !meeting) return <Loading label="Loading Agenda Builder…" />;

  const firstCategory = agenda[0]?.category?.key;
  const addItem = async (insight_id, category_key) => {
    setBusy(true);
    try {
      await api.post(`/meetings/${id}/agenda/items`, { insight_id, category_key: category_key || firstCategory });
      toast.success("Added to agenda"); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (item_id) => { setBusy(true); try { await api.delete(`/agenda-items/${item_id}`); await load(); } finally { setBusy(false); } };
  const moveWithin = async (grp, idx, dir) => {
    const items = grp.items; const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const reordered = [...items]; const [x] = reordered.splice(idx, 1); reordered.splice(j, 0, x);
    setBusy(true);
    try {
      await api.post(`/meetings/${id}/agenda/reorder`, {
        items: reordered.map((it, i) => ({ item_id: it.item_id, category_key: grp.category.key, order: i })),
      });
      await load();
    } finally { setBusy(false); }
  };
  const changeCategory = async (item_id, category_key) => {
    setBusy(true);
    try { await api.patch(`/agenda-items/${item_id}`, { category_key, order: 999 }); await load(); }
    finally { setBusy(false); }
  };
  const openPreview = async () => { const r = await api.get(`/meetings/${id}/agenda/preview`); setPreview(r.data); };
  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/meetings/${id}/publish`, { reason: "" });
      toast.success("Agenda published"); setConfirmPublish(false); navigate(`/meetings/${id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail?.message || e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const candidates = (eligible?.candidates || []).filter((c) => !q || c.title.toLowerCase().includes(q.toLowerCase()));
  const categories = agenda.map((g) => g.category);
  const allItems = agenda.flatMap((g) => g.items);

  return (
    <div data-testid="agenda-workspace">
      <Link to={`/meetings/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-navy mb-3">
        <ArrowLeft className="h-4 w-4" /> Back to Meeting
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-xl font-extrabold text-brand-ink">Agenda Builder</h1>
          <p className="text-sm text-slate-500">{meeting.title} · {fmtMeetingDate(meeting.date, meeting.start_time)}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="agenda-preview-btn" onClick={openPreview}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-line px-3 py-2 text-sm font-semibold text-brand-navy hover:bg-brand-bg">
            <Eye className="h-4 w-4" /> Preview
          </button>
          <button data-testid="agenda-publish-btn" disabled={!readiness?.ready} onClick={() => setConfirmPublish(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">
            <Send className="h-4 w-4" /> Publish Agenda
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        {/* LEFT: eligible insights */}
        <Card className="lg:col-span-4" data-testid="eligible-panel">
          <h2 className="font-display font-bold text-brand-ink text-sm mb-1">Eligible Insights</h2>
          {eligible?.deadline?.deadline_date && (
            <p className={`text-xs mb-2 ${eligible.deadline.passed ? "text-red-600" : "text-slate-400"}`}>
              Agenda deadline: {eligible.deadline.deadline_date} · {deadlineMessage(eligible.deadline)}
            </p>
          )}
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input data-testid="eligible-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" aria-label="Search eligible insights"
              className="w-full h-9 rounded-lg border border-brand-line bg-brand-bg pl-8 pr-2 text-sm outline-none focus:bg-white focus:border-brand-civic" />
          </div>
          {candidates.length === 0
            ? <p className="text-sm text-slate-400 py-6 text-center">No eligible Insights yet. Finalized Insights meeting this meeting's rules appear here.</p>
            : (
              <div className="space-y-2 max-h-[60vh] overflow-auto">
                {candidates.map((c) => {
                  const el = ELIGIBILITY[c.eligibility.state] || {};
                  const canAdd = c.eligibility.state === "ELIGIBLE";
                  return (
                    <div key={c.insight_id} data-testid="eligible-item" className="rounded-lg border border-brand-line p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-brand-ink truncate">{c.title}</div>
                          <div className="text-[11px] text-slate-400">{c.insight_type_code}</div>
                        </div>
                        <span className={`shrink-0 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${el.cls}`}>{el.label}</span>
                      </div>
                      {!canAdd && <p className="text-[11px] text-slate-500 mt-1">{c.eligibility.reason}</p>}
                      {canAdd && (
                        <button data-testid="add-to-agenda" disabled={busy} onClick={() => addItem(c.insight_id, firstCategory)}
                          className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-brand-civic-50 text-brand-civic px-2 py-1 text-xs font-semibold hover:bg-brand-civic/10 disabled:opacity-40">
                          <Plus className="h-3 w-3" /> Add to Agenda
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </Card>

        {/* CENTER: agenda */}
        <Card className="lg:col-span-5" data-testid="agenda-panel">
          <h2 className="font-display font-bold text-brand-ink text-sm mb-3">Agenda</h2>
          {allItems.length === 0
            ? <EmptyState icon={ListChecks} title="Agenda is empty" description="Add an eligible Insight from the left to begin." />
            : (
              <div className="space-y-4">
                {agenda.filter((g) => g.items.length > 0).map((grp) => (
                  <div key={grp.category.key}>
                    <div className="text-xs font-bold uppercase tracking-wider text-brand-civic mb-1.5">{grp.category.label}</div>
                    <div className="space-y-1.5">
                      {grp.items.map((it, idx) => (
                        <div key={it.item_id} data-testid="agenda-item" className="flex items-center gap-2 rounded-lg border border-brand-line px-3 py-2">
                          <span className="text-xs font-bold text-brand-navy w-8">{it.number}</span>
                          <Link to={`/insights/${it.insight_id}`} className="flex-1 text-sm text-brand-ink hover:text-brand-civic truncate">{it.insight_title}</Link>
                          <div className="flex items-center gap-1">
                            <button aria-label="Move up" data-testid="agenda-move-up" disabled={busy || idx === 0} onClick={() => moveWithin(grp, idx, -1)} className="text-slate-400 hover:text-brand-navy disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                            <button aria-label="Move down" data-testid="agenda-move-down" disabled={busy || idx === grp.items.length - 1} onClick={() => moveWithin(grp, idx, 1)} className="text-slate-400 hover:text-brand-navy disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                            <select aria-label="Category" data-testid="agenda-item-category" value={grp.category.key} onChange={(e) => changeCategory(it.item_id, e.target.value)} className="text-xs border border-brand-line rounded px-1 py-0.5 bg-white">
                              {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                            <button aria-label="Remove" data-testid="agenda-remove" disabled={busy} onClick={() => remove(it.item_id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </Card>

        {/* RIGHT: readiness */}
        <Card className="lg:col-span-3" data-testid="readiness-panel">
          <h2 className="font-display font-bold text-brand-ink text-sm mb-3">Readiness</h2>
          {!readiness ? <Loading /> : readiness.ready ? (
            <div data-testid="readiness-ready" className="rounded-lg border border-brand-teal/30 bg-brand-teal-50 p-3">
              <div className="flex items-center gap-2 text-brand-teal font-bold text-sm"><CheckCircle2 className="h-4 w-4" /> Ready to Publish</div>
              <p className="text-xs text-slate-600 mt-1">{readiness.item_count} item{readiness.item_count === 1 ? "" : "s"} · 0 blockers</p>
            </div>
          ) : (
            <div data-testid="readiness-blockers">
              <div className="flex items-center gap-2 text-amber-700 font-bold text-sm mb-2"><AlertTriangle className="h-4 w-4" /> {readiness.blockers.length} issue{readiness.blockers.length === 1 ? "" : "s"} to fix</div>
              <div className="space-y-2">
                {readiness.blockers.map((b, i) => (
                  <div key={i} data-testid="blocker-item" className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                    <p className="text-xs text-amber-800">{b.message}</p>
                    {b.action && <Link to={b.action.target} className="text-xs font-semibold text-brand-navy hover:underline mt-1 inline-block">{b.action.label} →</Link>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent data-testid="agenda-preview-dialog" className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader><DialogTitle>Agenda Preview</DialogTitle></DialogHeader>
          {preview && (
            <div className="text-sm">
              <div className="text-center border-b border-brand-line pb-3 mb-3">
                <div className="font-display font-bold text-brand-ink text-lg">{preview.meeting.governing_body_name}</div>
                <div className="text-brand-ink">{preview.meeting.title}</div>
                <div className="text-slate-500 text-xs">{fmtMeetingDate(preview.meeting.date, preview.meeting.start_time)} · {preview.meeting.location}</div>
              </div>
              {preview.agenda.filter((g) => g.items.length > 0).map((g) => (
                <div key={g.category.key} className="mb-3">
                  <div className="font-bold text-brand-navy">{g.category.label}</div>
                  {g.items.map((it) => <div key={it.item_id} className="ml-3 text-brand-ink">{it.number} — {it.insight_title}</div>)}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Publish confirm */}
      <Dialog open={confirmPublish} onOpenChange={setConfirmPublish}>
        <DialogContent data-testid="publish-confirm-dialog">
          <DialogHeader><DialogTitle>Publish this agenda?</DialogTitle>
            <DialogDescription>
              This creates the official public agenda snapshot for <strong>{meeting.title}</strong> on {fmtMeetingDate(meeting.date, meeting.start_time)}. Later Insight edits will not change this published snapshot.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setConfirmPublish(false)} className="rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-600 hover:bg-brand-bg">Cancel</button>
            <button data-testid="publish-confirm" disabled={busy} onClick={publish} className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">Publish Agenda</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
